import React, { useState } from "react";
import { ArrowRight, FileDown, Mail, Globe } from "lucide-react";
import styled, { keyframes, css } from "styled-components";
import linkedIcon from "../../assets/svgs/linkedin-icon.svg";
import githubIcon from "../../assets/svgs/github-icon.svg";
import Modal from "../Modal";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideIn = keyframes`
  from { 
    transform: scale(0.95) translateY(-20px);
    opacity: 0;
  }
  to { 
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from { 
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  to { 
    transform: scale(0.95) translateY(20px);
    opacity: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 3rem;
  margin-bottom: 3rem;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2.5rem;
  }
`;

const ContactButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;

  @media (max-width: 768px) {
    margin: auto;
  }
`;

const CTAButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 500;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
`;

const CTAIcon = styled.img`
  height: 100%;
  width: 100%;
`;

const ContactButton = styled.a`
  height: 3.5rem;
  width: 3.5rem;
  padding: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
`;

const ModalWrapper = styled.div`
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  animation: ${({ isClosing }) =>
    isClosing
      ? css`
          ${fadeOut} 0.3s ease-in-out forwards
        `
      : css`
          ${fadeIn} 0.3s ease-in-out
        `};

  & > div > div {
    animation: ${({ isClosing }) =>
      isClosing
        ? css`
            ${slideOut} 0.3s ease-in-out forwards
          `
        : css`
            ${slideIn} 0.3s ease-in-out
          `};
  }
`;

const CTASection = ({ links }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleOpenModal = () => {
    setIsClosing(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsClosing(false);
    }, 300);
  };

  // Define known platforms and their icons
  const knownPlatforms = {
    linkedin: linkedIcon,
    github: githubIcon,
    email: <Mail />,
  };

  return (
    <div>
      {isModalOpen && (
        <ModalWrapper isVisible={isModalOpen} isClosing={isClosing}>
          <Modal isOpen={true} onClose={handleCloseModal} />
        </ModalWrapper>
      )}

      <ButtonContainer>
        <CTAButtonsWrapper>
          <CTAButton href="#projects">
            Projects
            <ArrowRight size={20} />
          </CTAButton>
          <CTAButton onClick={handleOpenModal}>
            Download CV
            <FileDown size={20} />
          </CTAButton>
        </CTAButtonsWrapper>

        <ContactButtonsWrapper>
          {Object.entries(links).map(([key, url]) => {
            if (!url) return null;

            return (
              <ContactButton key={key} href={key === "email" ? `mailto:${url}` : url} target="_blank" rel="noopener noreferrer">
                {knownPlatforms[key] ? (
                  typeof knownPlatforms[key] === "string" ? (
                    <CTAIcon src={knownPlatforms[key]} alt={key} />
                  ) : (
                    knownPlatforms[key]
                  )
                ) : (
                  <Globe /> // Default icon for unknown links
                )}
              </ContactButton>
            );
          })}
        </ContactButtonsWrapper>
      </ButtonContainer>
    </div>
  );
};

export default CTASection;
